import React from 'react';
import PropTypes from 'prop-types';
import { roleAlignmentRows } from './RoleAlignmentForm';
import { MANAGERIAL_ASSESSMENT_SCHEMA } from './ManagerialAssessmentForm';

/* Print / download for one person's Role Alignment result.
 *
 * ONE PERSON AT A TIME, DELIBERATELY. Both buttons act on the result already on
 * screen, so neither can hand you anything you were not already allowed to read.
 * That matters here more than usual: the API keeps these answers to the DIRECT
 * manager (a skip-level manager sees that the form is done, not what it says)
 * because they are candid self-assessments. A bulk export would quietly route
 * around that, so there isn't one.
 *
 * PRINT IS ALSO THE PDF. Every browser's print dialog offers "Save as PDF", so a
 * print stylesheet gets the filing-a-copy case without pulling in a PDF
 * generator and a second rendering of the form to keep in step with the first.
 *
 * The CSV is for the reader who wants the answers in a document — it is the
 * questionnaire and the manager's assessment as section / question / answer
 * rows, built from the same schema walk the page renders, so the two cannot
 * drift apart.
 */

// Applied to <body> for the duration of a print. Scoped that way so an ordinary
// Ctrl+P still prints the whole page as before — only our button narrows it.
const PRINT_CLASS = 'printing-result';

export function printResult() {
  document.body.classList.add(PRINT_CLASS);
  const cleanup = () => {
    document.body.classList.remove(PRINT_CLASS);
    window.removeEventListener('afterprint', cleanup);
  };
  window.addEventListener('afterprint', cleanup);
  // `afterprint` is unreliable in older Safari, and a body left in print mode
  // renders the app as a blank page. The timer is the backstop; whichever fires
  // first wins and the other is a no-op.
  setTimeout(cleanup, 60000);
  window.print();
}

const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

function download(filename, text) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Next tick — revoking synchronously can cancel the download in Safari.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// Safe for a filename on every OS, and readable afterwards.
const slug = (s) => (s || 'result').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);

const btn = {
  background: 'white', border: '1px solid #cdd5dd', color: '#0a2947',
  borderRadius: 6, padding: '0.3rem 0.7rem', fontSize: '0.78rem', fontWeight: 700,
  cursor: 'pointer',
};

export default function ResultActions({ name, submission, assessment }) {
  const rows = [];
  // Guarded rather than passed straight in: a manager who was asked for an
  // assessment but not shown the questionnaire gets a submission with no
  // `responses` at all, and walking the schema over nothing would write out
  // every question with a blank answer — a CSV that looks like the person left
  // the form empty. No answers, no section.
  if (submission?.responses) {
    roleAlignmentRows(submission.responses).forEach((r) =>
      rows.push({ form: 'Role Alignment', ...r }));
  }
  // Appended rather than a second file: they are read together, and two
  // downloads for one conversation is two things to keep track of.
  if (assessment?.responses) {
    roleAlignmentRows(assessment.responses, MANAGERIAL_ASSESSMENT_SCHEMA).forEach((r) =>
      rows.push({ form: 'Managerial Assessment', ...r }));
  }

  const onDownload = () => {
    const header = ['form', 'section', 'question', 'answer'];
    const meta = [
      ['', '', 'Employee', name || ''],
      ['', '', 'Submitted', submission?.created ? new Date(submission.created).toLocaleString() : ''],
      ['', '', 'Assessment by', assessment?.submitter_name || ''],
      ['', '', 'Assessment filed', assessment?.created ? new Date(assessment.created).toLocaleString() : ''],
      ['', '', '', ''],
    ];
    const body = rows.map((r) => [r.form, r.section, r.question, r.answer]);
    const csv = [header, ...meta, ...body].map((r) => r.map(csvCell).join(',')).join('\n');
    download(`role-alignment-${slug(name)}.csv`, csv);
  };

  return (
    // no-print: these are controls, and a printout of a form should not show
    // buttons that cannot be pressed.
    <div className="no-print" style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
      <button type="button" style={btn} onClick={printResult}>
        <i className="bi bi-printer me-1" />Print
      </button>
      <button type="button" style={btn} onClick={onDownload} disabled={rows.length === 0}>
        <i className="bi bi-download me-1" />Download CSV
      </button>
      <span style={{ color: '#58585b', fontSize: '0.72rem', alignSelf: 'center' }}>
        Print offers &ldquo;Save as PDF&rdquo;.
      </span>
    </div>
  );
}
ResultActions.propTypes = {
  name: PropTypes.string,
  submission: PropTypes.object,
  assessment: PropTypes.object,
};
